import { useTranslation } from 'react-i18next';

/**
 * Encapsulates the practice-summary "state" logic so the ChatbotPracticeSummary
 * component stays pure JSX. Given the session's per-activity results, it derives
 * the performance state (success / progress / failure) and resolves every piece
 * of text and styling the screen needs.
 */

// Performance thresholds (by exact % of correct answers) that drive the summary state.
// No rounding at the boundary: 50% stays "in progress" (one right out of two is not a
// failure); only strictly below half falls into "multiple mistakes".
const SUCCESS_THRESHOLD = 80;
const PROGRESS_THRESHOLD = 50;

type SummaryState = 'success' | 'progress' | 'failure';

type SummaryStateConfig = {
  titleKey: string;
  skillsTitleKey: string;
  robotModifier: SummaryState;
  robotLabelKey: string;
};

// A single state reframes the whole screen: title, robot illustration and skills header.
// The skills list itself is always the same — only the header wording changes.
const SUMMARY_STATE_CONFIG: Record<SummaryState, SummaryStateConfig> = {
  success: {
    titleKey: 'practice_summary_good_job',
    skillsTitleKey: 'practice_summary_learned',
    robotModifier: 'success',
    robotLabelKey: 'practice_ai_bot_celebrating',
  },
  progress: {
    titleKey: 'practice_summary_on_track',
    skillsTitleKey: 'practice_summary_learning',
    robotModifier: 'progress',
    robotLabelKey: 'practice_ai_bot_writing',
  },
  failure: {
    titleKey: 'practice_summary_keep_practicing',
    skillsTitleKey: 'practice_summary_improvement',
    robotModifier: 'failure',
    robotLabelKey: 'practice_ai_bot_effort',
  },
};

type ActivityResult = {
  isCorrect?: boolean;
};

const getSummaryState = (activityResults: ActivityResult[]): SummaryState => {
  const total = activityResults.length;
  const correct = activityResults.filter((result) => result.isCorrect).length;
  const successRate = total > 0 ? (correct / total) * 100 : 0;

  if (successRate >= SUCCESS_THRESHOLD) return 'success';
  if (successRate >= PROGRESS_THRESHOLD) return 'progress';
  return 'failure';
};

type UseChatbotPracticeSummaryParams = {
  activityResults?: ActivityResult[];
  firstName?: string;
};

type UseChatbotPracticeSummaryResult = {
  title: string;
  skillsTitle: string;
  answersTitle: string;
  robotModifier: SummaryState;
  robotLabel: string;
};

const useChatbotPracticeSummary = ({ activityResults = [], firstName }: UseChatbotPracticeSummaryParams): UseChatbotPracticeSummaryResult => {
  const { t } = useTranslation(['chatbot']);

  const summaryState = getSummaryState(activityResults);
  const { titleKey, skillsTitleKey, robotModifier, robotLabelKey } = SUMMARY_STATE_CONFIG[summaryState];

  return {
    title: t(titleKey, { firstName }),
    skillsTitle: t(skillsTitleKey),
    answersTitle: t('practice_summary_your_answers'),
    robotModifier,
    robotLabel: t(robotLabelKey),
  };
};

export default useChatbotPracticeSummary;
